import { getSearchBounds } from '@bridebook/web/app-basic/lib/search/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { ReplaySubject, Subject, Subscription, of } from 'rxjs';
import { debounceTime as debounceTimeOperator, switchMap, withLatestFrom } from 'rxjs/operators';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import type { Slug } from '@bridebook/toolbox/src/types';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { ILatLongBounds } from '@bridebook/web/app-shared/lib/search/types';
import {
  AutocompletePredictionsService,
  IGetAutocompletePredictionsOptions,
} from '@bridebook/web/app-shared/lib/search/utils/autocomplete-predictions-service';
import { CustomPredictionService } from '@bridebook/web/app-shared/lib/search/utils/custom-autocomplete-predictions/custom-prediction-service';
import { GooglePlacesService } from '@bridebook/web/app-shared/lib/search/utils/google-places-service';
import { GooglePredictionsContext } from './context';

const DEBOUNCE_TIME = 250;
const MINIMAL_INPUT_LENGTH = 2;
const googlePlacesService = new GooglePlacesService();
const autocompletePredictionsService = new AutocompletePredictionsService(
  new CustomPredictionService(),
  googlePlacesService,
);

export interface AutocompletePredictions {
  /**
   * Debounce time for input. Default set to 250ms.
   */
  debounceTime?: number;
  /**
   * Narrow down the search to specific types of places. Default set to undefined.
   */
  types?: IGetAutocompletePredictionsOptions['types'];
  /**
   * Minimal input length to trigger the autocomplete predictions. Default set to 2.
   */
  minInputLength?: number;
  /**
   * * Allows overriding the default user/wedding market country with a specified country.
   */
  country?: CountryCodes;
  /**
   * Force enable global autocomplete. Default set to false.
   */
  forceEnableGlobalAutocomplete?: boolean;
}

/**
 * Uses googlePlacesService to set the GooglePredictionsContext values based on user input.
 */
export const useSetAutocompletePredictions = (
  input: string,
  {
    country = undefined,
    minInputLength = MINIMAL_INPUT_LENGTH,
    debounceTime = DEBOUNCE_TIME,
    types = undefined,
  }: AutocompletePredictions = {},
) => {
  // Set debounce value.
  const valuesSubject$ = useRef(new Subject<string>());
  const valuesSubjectSubscription = useRef<Subscription>();
  const categoryValue = 'venue';
  const categorySubject$ = useRef(new ReplaySubject<Slug>());
  const countrySubject$ = useRef(new ReplaySubject<CountryCodes | undefined>());
  const market = useMarket();
  const countryCode = country || market.country;

  // push input value to valuesSubject$ stream
  useEffect(() => {
    valuesSubject$.current.next(input);
  }, [input]);

  // keep track of category
  useEffect(() => {
    categorySubject$.current.next(categoryValue);
  }, [categoryValue]);

  useEffect(() => {
    countrySubject$.current.next(countryCode);
  }, [countryCode]);

  const { setPredictions } = useContext(GooglePredictionsContext);

  const bounds = useSelector(getSearchBounds) as ILatLongBounds;

  const getAutocompleteValues = useCallback(
    async (input: string, category: Slug | '', countryCode?: CountryCodes) =>
      autocompletePredictionsService.getAutocompletePredictions(input, {
        countryCode,
        bounds,
        category,
        types,
      }),
    [bounds, types],
  );

  useEffect(() => {
    countrySubject$.current.subscribe(() => {
      setPredictions([], {}, '');
    });
  }, [setPredictions]);

  useEffect(() => {
    // prevents from subscribing multiple times and sending multiple request to maps api
    if (valuesSubjectSubscription.current) return;

    valuesSubjectSubscription.current = valuesSubject$.current
      .pipe(
        debounceTimeOperator(debounceTime),
        withLatestFrom(categorySubject$.current),
        withLatestFrom(countrySubject$.current),
        switchMap(([[currentInput, category], country]) =>
          currentInput && currentInput.length >= minInputLength
            ? getAutocompleteValues(currentInput, category as Slug, country)
            : of({ predictions: [], googlePredictions: {}, input: currentInput }),
        ),
      )
      .subscribe(({ predictions, googlePredictions, input }) => {
        setPredictions(predictions, googlePredictions, input);
      });
  }, [debounceTime, getAutocompleteValues, input, minInputLength, setPredictions]);

  // unsubscribe on unmount
  useEffect(
    () => () => {
      valuesSubjectSubscription.current?.unsubscribe();
    },
    [],
  );
};
